import { useEffect, useState } from 'react'
import { api, type Fakt, type PersonUebersicht } from '../api'
import { link } from '../navigation'

type Details = { person: PersonUebersicht; fakten: Fakt[] }

function datum(f: Fakt) {
  if (!f.aufgenommenAm) return null
  return new Date(f.aufgenommenAm).toLocaleDateString('de-DE', { timeZone: f.zeitzone ?? undefined })
}

export function PersonDetail({ id }: { id: number }) {
  const [details, setDetails] = useState<Details | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [bearbeiten, setBearbeiten] = useState(false)
  const [zusammenfuehren, setZusammenfuehren] = useState(false)
  const [meldung, setMeldung] = useState<string | null>(null)

  useEffect(() => {
    api.person(id).then(setDetails).catch((e) => setFehler((e as Error).message))
  }, [id])

  const aktion = async (f: () => Promise<void>) => {
    setFehler(null)
    try {
      await f()
    } catch (e) {
      setFehler((e as Error).message)
    }
  }

  if (!details) return fehler ? <p className="fehler">{fehler}</p> : null
  const { person, fakten } = details

  return (
    <div className="person-detail">
      <a href="#personen" className="zurueck">
        ‹ Alle Personen
      </a>

      {bearbeiten ? (
        <PersonFormular
          person={person}
          onAbbrechen={() => setBearbeiten(false)}
          onSpeichern={(e) =>
            aktion(async () => {
              setDetails(await api.personAendern(id, e))
              setBearbeiten(false)
            })
          }
        />
      ) : (
        <section className="karte">
          <h2>{person.name}</h2>
          {person.aliase.length > 0 && <p className="leise">auch: {person.aliase.join(', ')}</p>}
          {person.aliase.length > 0 && (
            <details className="alias-loesen">
              <summary className="leise klein">Falsch zugeordnet?</summary>
              <p className="leise klein">
                Hat die App einen Namen versehentlich {person.name} zugeordnet, löse ihn hier: Der Name fällt weg, die Notizen mit diesem
                Namen werden neu ausgewertet und fragen bei Unklarheit erneut nach. Die Beschreibung musst du bei Bedarf selbst bereinigen.
              </p>
              <div className="knoepfe links">
                {person.aliase.map((a) => (
                  <button
                    key={a}
                    className="link gefahr"
                    onClick={() =>
                      confirm(`„${a}“ von ${person.name} lösen? Die betroffenen Notizen werden neu ausgewertet.`) &&
                      aktion(async () => {
                        const r = await api.personAliasLoesen(id, a)
                        setDetails(await api.person(id))
                        setMeldung(
                          r.notizen === 0
                            ? `„${a}“ ist gelöst. Keine Notiz war betroffen.`
                            : `„${a}“ ist gelöst. ${r.notizen === 1 ? 'Eine Notiz wird' : `${r.notizen} Notizen werden`} neu ausgewertet; die Rückfragen erscheinen gleich unter „Personen“.`,
                        )
                      })
                    }
                  >
                    „{a}“ lösen
                  </button>
                ))}
              </div>
            </details>
          )}
          {person.beschreibung && <p>{person.beschreibung}</p>}
          <div className="knoepfe links">
            <button className="zweitrangig" onClick={() => setBearbeiten(true)}>
              Bearbeiten
            </button>
            <a className="knopf zweitrangig" href={link.notizenVon(id)}>
              Notizen
            </a>
          </div>
        </section>
      )}

      {fehler && <p className="fehler">{fehler}</p>}
      {meldung && <p className="hinweis">{meldung}</p>}

      <h3>Was du über {person.name} weißt</h3>
      {fakten.length === 0 && <p className="leise">Noch nichts.</p>}
      <ul className="fakten-liste">
        {fakten.map((f) => (
          <li key={f.id} className="karte">
            <span>{f.fakt}</span>
            <span className="leise klein">
              {f.notizId && datum(f) && <a href={link.notizenVon(id)}>{datum(f)}</a>}
              <button
                className="link gefahr"
                aria-label="Fakt löschen"
                onClick={() =>
                  confirm(`„${f.fakt}“ löschen?`) &&
                  aktion(async () => {
                    await api.faktLoeschen(id, f.id)
                    setDetails({ person, fakten: fakten.filter((x) => x.id !== f.id) })
                  })
                }
              >
                ✕
              </button>
            </span>
          </li>
        ))}
      </ul>

      <h3>Verwalten</h3>
      {zusammenfuehren ? (
        <Zusammenfuehren
          person={person}
          onAbbrechen={() => setZusammenfuehren(false)}
          onGewaehlt={(mitId) =>
            aktion(async () => {
              setDetails(await api.personenZusammenfuehren(id, mitId))
              setZusammenfuehren(false)
            })
          }
        />
      ) : (
        <div className="knoepfe links">
          <button className="zweitrangig" onClick={() => setZusammenfuehren(true)}>
            Doppelt? Zusammenführen …
          </button>
          <button
            className="link gefahr"
            onClick={() =>
              confirm(`${person.name} mit allen Fakten löschen? Die Notizen bleiben erhalten.`) &&
              aktion(async () => {
                await api.personLoeschen(id)
                window.location.hash = '#personen'
              })
            }
          >
            Person löschen
          </button>
        </div>
      )}
    </div>
  )
}

function PersonFormular({
  person,
  onSpeichern,
  onAbbrechen,
}: {
  person: PersonUebersicht
  onSpeichern: (e: { name: string; beschreibung: string; aliase: string[] }) => void
  onAbbrechen: () => void
}) {
  const [name, setName] = useState(person.name)
  const [beschreibung, setBeschreibung] = useState(person.beschreibung)
  const [aliase, setAliase] = useState(person.aliase.join(', '))

  return (
    <form
      className="karte formular"
      onSubmit={(e) => {
        e.preventDefault()
        onSpeichern({ name, beschreibung, aliase: aliase.split(',').map((a) => a.trim()).filter(Boolean) })
      }}
    >
      <label>
        Name
        <input value={name} onChange={(e) => setName(e.target.value)} required />
      </label>
      <label>
        Weitere Namen (durch Komma getrennt)
        <input value={aliase} onChange={(e) => setAliase(e.target.value)} />
      </label>
      <label>
        Beschreibung
        <textarea rows={2} value={beschreibung} onChange={(e) => setBeschreibung(e.target.value)} />
      </label>
      <div className="knoepfe">
        <button type="button" className="zweitrangig" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button type="submit">Speichern</button>
      </div>
    </form>
  )
}

function Zusammenfuehren({
  person,
  onGewaehlt,
  onAbbrechen,
}: {
  person: PersonUebersicht
  onGewaehlt: (mitId: number) => void
  onAbbrechen: () => void
}) {
  const [andere, setAndere] = useState<PersonUebersicht[]>([])
  const [auswahl, setAuswahl] = useState<number | null>(null)

  useEffect(() => {
    api.personen().then((p) => setAndere(p.filter((x) => x.id !== person.id)))
  }, [person.id])

  const gewaehlt = andere.find((p) => p.id === auswahl)

  return (
    <div className="karte formular">
      <label>
        Diese Person ist dieselbe wie {person.name}:
        <select value={auswahl ?? ''} onChange={(e) => setAuswahl(Number(e.target.value) || null)}>
          <option value="">– bitte wählen –</option>
          {andere.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.beschreibung ? ` – ${p.beschreibung}` : ''}
            </option>
          ))}
        </select>
      </label>
      {gewaehlt && (
        <p className="leise klein">
          Fakten und Notizen von {gewaehlt.name} wandern zu {person.name}, „{gewaehlt.name}“ wird zum weiteren Namen.
        </p>
      )}
      <div className="knoepfe">
        <button className="zweitrangig" onClick={onAbbrechen}>
          Abbrechen
        </button>
        <button disabled={!gewaehlt} onClick={() => gewaehlt && onGewaehlt(gewaehlt.id)}>
          Zusammenführen
        </button>
      </div>
    </div>
  )
}
